// ✅ src/pages/Sales/PartPaymentReport.jsx
// FULL FILE - LOCALSTORAGE HEADERS ADDED + SEARCH/PRINT FIXED

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaChevronUp,
  FaChevronDown,
  FaUser,
  FaHashtag,
  FaCalendarAlt,
  FaPrint,
} from "react-icons/fa";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const PartPaymentReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = `bg-${baseColor}-${baseShade}`;

  const [showFilters, setShowFilters] = useState(true);
  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // Search form state
  const [searchData, setSearchData] = useState({
    enquiryNumber: "",
    customerName: "",
    customerId: "",
    assetSubType: "",
    doB: "",
    paymentStatus: "",
  });

  // Listing rows from API
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Asset Sub Type dropdown from API
  const [assetSubTypes, setAssetSubTypes] = useState([]);
  const [assetTypeLoading, setAssetTypeLoading] = useState(false);

  const paymentStatuses = ["Completed", "Pending", "Processing", "Overdue"];

  // ---------------------------
  // Helpers
  // ---------------------------
  const handleInputChange = (field, value) => {
    setSearchData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatAmount = (amount) => {
    const n = Number(amount || 0);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(n);
  };

  const getPaymentStatusColor = (status) => {
    switch (String(status || "").trim().toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const buildSearchParamsObject = () => ({
    enquiryNumber: searchData.enquiryNumber || "",
    customerName: searchData.customerName || "",
    customerId: searchData.customerId || "",
    assetSubType: searchData.assetSubType || "",
    doB: searchData.doB || "",
    paymentStatus: searchData.paymentStatus || "",
  });

  const buildSearchParams = (obj) => {
    const params = new URLSearchParams();
    Object.entries(obj || {}).forEach(([k, v]) => {
      if (v !== null && v !== undefined && String(v).trim() !== "") {
        params.set(k, String(v).trim());
      }
    });
    return params;
  };

  // ---------------------------
  // AssetSubType API call on load
  // ---------------------------
  useEffect(() => {
    const fetchAssetSubTypes = async () => {
      try {
        setAssetTypeLoading(true);

        const res = await axios.get(`${BASE_URL}/api/partListing/assetSubTypes`, {
          headers: commonHeaders,
        });

        const list = res?.data?.data ?? res?.data ?? [];
        setAssetSubTypes(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load Asset Sub Types");
        setAssetSubTypes([]);
      } finally {
        setAssetTypeLoading(false);
      }
    };

    fetchAssetSubTypes();
  }, [commonHeaders]);

  // ---------------------------
  // Search API call
  // ---------------------------
  const handleSearch = async () => {
    const hasSearchCriteria = Object.values(searchData).some(
      (value) => String(value).trim() !== ""
    );

    if (!hasSearchCriteria) {
      toast.warning("Please enter at least one search criteria!");
      return;
    }

    try {
      setLoading(true);

      const params = buildSearchParamsObject();

      const res = await axios.get(`${BASE_URL}/api/partListing/search`, {
        params,
        headers: commonHeaders,
      });

      console.log("res", res.data?.data);

      const data = res?.data?.data ?? res?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
      toast.success("Search completed!");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Search failed. Check API / server logs.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // Print All
  // ---------------------------
  const handleAllPrint = () => {
    const hasSearchCriteria = Object.values(searchData).some(
      (value) => String(value).trim() !== ""
    );

    if (!hasSearchCriteria) {
      toast.warning("Please enter at least one search criteria before Print All!");
      return;
    }

    const qs = buildSearchParams({
      ...buildSearchParamsObject(),
      finYearId,
      finYear,
      branchId,
      branch,
      userId,
    }).toString();

    window.open(`/Sales/PartPaymentReport_AllPrint?${qs}`, "_blank");
  };

  // ---------------------------
  // Row Print
  // ---------------------------
  const handleRowPrint = (row) => {
    const id =
      row?.paymentId ??
      row?.fld_paymentid ??
      row?.customerId ??
      row?.fld_customerid;

    if (!id) {
      toast.error("No id found in row to print!");
      return;
    }

    const qs = new URLSearchParams({
      id: String(id),
      finYearId,
      finYear,
      branchId,
      branch,
      userId,
    }).toString();

    window.open(`/Sales/PartPaymentReport_Print?${qs}`, "_blank");
  };

  // ---------------------------
  // Normalize rows for table display
  // ---------------------------
  const mappedRows = useMemo(() => {
    return rows.map((r, idx) => ({
      slNo: idx + 1,
      customerId: r?.customerId ?? r?.fld_customerid ?? "",
      customerName: r?.customerName ?? r?.fld_customername ?? "",
      plotNumber: r?.fld_plotnumber ?? r?.fld_plotno ?? "",
      assetCode: r?.assetCode ?? r?.fld_assetcode ?? "",
      assetTypeModel: r?.assetTypeModel ?? r?.fld_assettypemodel ?? "",
      amount: r?.amount ?? r?.fld_amount ?? 0,
      balAmount: r?.balAmount ?? r?.fld_balanceamount ?? 0,
      paymentStatus: r?.fld_paystatus ?? r?.fld_paymentstatus ?? r?.paymentStatus ?? "",
      _raw: r,
    }));
  }, [rows]);

  const totalAmount = useMemo(
    () => mappedRows.reduce((sum, x) => sum + Number(x.amount || 0), 0),
    [mappedRows]
  );

  const totalBal = useMemo(
    () => mappedRows.reduce((sum, x) => sum + Number(x.balAmount || 0), 0),
    [mappedRows]
  );

  const pendingCount = useMemo(
    () =>
      mappedRows.filter(
        (x) => String(x.paymentStatus || "").trim().toLowerCase() === "pending"
      ).length,
    [mappedRows]
  );

  return (
    <SalesLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PART PAYMENT CUSTOMER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Part Payment Report
            </h1>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                Search Criteria
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaHashtag className="inline mr-2" />
                    Enquiry Number
                  </label>
                  <input
                    type="text"
                    placeholder="Enter enquiry number"
                    value={searchData.enquiryNumber}
                    onChange={(e) => handleInputChange("enquiryNumber", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaUser className="inline mr-2" />
                    Customer Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter customer name"
                    value={searchData.customerName}
                    onChange={(e) => handleInputChange("customerName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaHashtag className="inline mr-2" />
                    Customer ID
                  </label>
                  <input
                    type="text"
                    placeholder="Enter customer ID"
                    value={searchData.customerId}
                    onChange={(e) => handleInputChange("customerId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Asset Sub Type
                  </label>

                  <select
                    value={searchData.assetSubType}
                    onChange={(e) => handleInputChange("assetSubType", e.target.value)}
                    disabled={assetTypeLoading}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">{assetTypeLoading ? "Loading..." : "Select"}</option>

                    {assetSubTypes.map((obj, index) => {
                      const optionValue =
                        obj?.fld_assetsubtypeid ??
                        obj?.id ??
                        obj?.value ??
                        obj?.fld_assetsubtypename ??
                        "";
                      const optionLabel =
                        obj?.fld_assetsubtypename ??
                        obj?.name ??
                        obj?.label ??
                        obj?.assetSubTypeName ??
                        "";

                      return (
                        <option key={optionValue || index} value={optionValue}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaCalendarAlt className="inline mr-2" />
                    DoB
                  </label>
                  <input
                    type="date"
                    value={searchData.doB}
                    onChange={(e) => handleInputChange("doB", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Payment Status
                  </label>
                  <select
                    value={searchData.paymentStatus}
                    onChange={(e) => handleInputChange("paymentStatus", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {paymentStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center gap-6">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  onClick={handleAllPrint}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPrint />
                  Print
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Sl No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Customer ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Customer Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Plot Number</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Asset Code</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Asset Type / Model</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Bal Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Payment Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Print</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {mappedRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className={`px-6 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {loading ? "Loading..." : "No data"}
                    </td>
                  </tr>
                ) : (
                  mappedRows.map((row, index) => (
                    <tr
                      key={index}
                      className={`${
                        index % 2 === 0
                          ? isDark
                            ? "bg-gray-800"
                            : "bg-white"
                          : isDark
                          ? "bg-gray-750"
                          : "bg-gray-50"
                      } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"} transition-colors`}
                    >
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{row.slNo}</td>
                      <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>{row.customerId}</td>
                      <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{row.customerName}</td>
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{row.plotNumber}</td>
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{row.assetCode}</td>
                      <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{row.assetTypeModel}</td>
                      <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                        {formatAmount(row.amount)}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm font-semibold ${
                          Number(row.balAmount) > 0
                            ? isDark
                              ? "text-red-400"
                              : "text-red-600"
                            : isDark
                            ? "text-green-400"
                            : "text-green-600"
                        }`}
                      >
                        {formatAmount(row.balAmount)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(
                            row.paymentStatus
                          )}`}
                        >
                          {row.paymentStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          type="button"
                          onClick={() => handleRowPrint(row._raw)}
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                            isDark
                              ? "bg-gray-700 hover:bg-gray-600 text-white"
                              : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                          }`}
                        >
                          <FaPrint />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div
            className={`mt-6 p-4 rounded-lg border ${
              isDark ? "bg-gray-800/30 border-gray-600" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <h4 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Search Results
                </h4>
                <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Showing {mappedRows.length} part payment customers
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className={`p-3 rounded-lg ${isDark ? "bg-green-900/30" : "bg-green-50"}`}>
                  <div className="text-lg font-bold text-green-600">{formatAmount(totalAmount)}</div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>Total Amount</div>
                </div>

                <div className={`p-3 rounded-lg ${isDark ? "bg-red-900/30" : "bg-red-50"}`}>
                  <div className="text-lg font-bold text-red-600">{formatAmount(totalBal)}</div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>Balance Amount</div>
                </div>

                <div className={`p-3 rounded-lg ${isDark ? "bg-yellow-900/30" : "bg-yellow-50"}`}>
                  <div className="text-lg font-bold text-yellow-600">{pendingCount}</div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>Pending</div>
                </div>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default PartPaymentReport;